import { Card, CardHeader, CardContent, Box, Typography, Skeleton } from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import { DonutLarge } from '@mui/icons-material';

interface RevenueStream {
  name: string;
  value: number;
  color?: string;
}

interface RevenueStreamsChartProps {
  data: RevenueStream[];
  isLoading?: boolean;
  title?: string;
  subtitle?: string;
}

const COLORS = [
  '#6c63ff',
  '#667eea',
  '#2ec4b6',
  '#ff9f1c',
  '#e71d36',
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#8b5cf6',
  '#ec4899',
];

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const percentage = payload[0].payload.percentage || 0;
    return (
      <Box
        sx={{
          bgcolor: 'rgba(31, 41, 55, 0.95)',
          backdropFilter: 'blur(10px)',
          color: 'white',
          p: 2,
          borderRadius: 2,
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          border: `2px solid ${payload[0].payload.color}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: payload[0].payload.color,
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {payload[0].name}
          </Typography>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: payload[0].payload.color }}>
          KES {payload[0].value?.toLocaleString()}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {percentage.toFixed(1)}% of total
        </Typography>
      </Box>
    );
  }
  return null;
};

const renderCustomLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: any) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  if (percent < 0.05) return null; // Don't show label for small slices

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      style={{ fontSize: '0.8125rem', fontWeight: 700, textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const CustomLegend = ({ payload }: any) => {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center', mt: 2 }}>
      {payload.map((entry: any, index: number) => (
        <Box
          key={`legend-${index}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.75,
            bgcolor: 'rgba(248, 249, 255, 0.8)',
            borderRadius: 1.5,
            border: `1px solid ${entry.color}20`,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: `${entry.color}15`,
              transform: 'translateY(-1px)',
            },
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: entry.color,
              boxShadow: `0 0 8px ${entry.color}60`,
            }}
          />
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#1f2937', fontSize: '0.75rem' }}>
            {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const RevenueStreamsChart = ({
  data,
  isLoading = false,
  title = 'Revenue Streams',
  subtitle = 'Breakdown by source',
}: RevenueStreamsChartProps) => {
  // Default data with placeholder categories showing equal distribution
  const defaultData: RevenueStream[] = [
    { name: 'Licenses & Permits', value: 20, color: COLORS[0] },
    { name: 'Property Rates', value: 20, color: COLORS[1] },
    { name: 'Land Rent', value: 20, color: COLORS[2] },
    { name: 'Parking Fees', value: 20, color: COLORS[3] },
    { name: 'Other Services', value: 20, color: COLORS[4] },
  ];

  // Check if data has actual values (not all zeros)
  const hasActualData = data && data.length > 0 && data.some(item => item.value > 0);
  const sourceData = hasActualData ? data : defaultData;

  if (isLoading) {
    return (
      <Card
        sx={{
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200',
          height: '100%',
        }}
      >
        <CardHeader
          title={<Skeleton width={150} />}
          subheader={<Skeleton width={100} />}
          sx={{
            '& .MuiCardHeader-title': {
              fontWeight: 600,
              fontSize: '1.125rem',
            },
          }}
        />
        <CardContent sx={{ pt: 2 }}>
          <Skeleton variant="circular" width={250} height={250} sx={{ mx: 'auto' }} />
        </CardContent>
      </Card>
    );
  }

  const totalValue = sourceData.reduce((sum, item) => sum + item.value, 0);

  const chartData = sourceData.map((item, index) => ({
    ...item,
    color: item.color || COLORS[index % COLORS.length],
    percentage: totalValue > 0 ? (item.value / totalValue) * 100 : 0,
  }));

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200',
        height: '100%',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
        boxShadow: '0 2px 8px rgba(108, 99, 255, 0.08)',
        transition: 'all 0.3s ease',
        animation: 'slideInUp 0.6s ease-out',
        '@keyframes slideInUp': {
          from: {
            opacity: 0,
            transform: 'translateY(20px)',
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
        '&:hover': {
          boxShadow: '0 8px 24px rgba(108, 99, 255, 0.15)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #2ec4b6 0%, #1ba098 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DonutLarge sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', color: '#1f2937' }}>
                {title}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                {subtitle}
              </Typography>
            </Box>
          </Box>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 0 }}>
        <Box sx={{ position: 'relative' }}>
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={hasActualData ? renderCustomLabel : () => null}
                outerRadius={100}
                innerRadius={70}
                fill="#8884d8"
                dataKey="value"
                paddingAngle={3}
                animationBegin={200}
                animationDuration={800}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
            </PieChart>
          </ResponsiveContainer>

          {/* Center total */}
          <Box
            sx={{
              position: 'absolute',
              top: '40%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Total
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: hasActualData ? '#6c63ff' : '#94a3b8', mt: 0.5 }}>
              {hasActualData ? totalValue.toLocaleString() : '0'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.625rem', mt: 0.25 }}>
              KES
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
